import React, { useState, useMemo, useContext, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { FeatureType } from '../../constants/FeatureToggles';
import {
  MDBContainer,
  MDBRow,
  MDBCol,
  MDBListGroup,
  MDBListGroupItem,
  MDBCard,
  MDBCardBody,
  MDBCardTitle,
  MDBBtn,
  MDBAccordion,
  MDBAccordionItem,
  MDBInput,
  MDBTextArea
} from 'mdb-react-ui-kit';
import { Edit as EditIcon, Visibility, VisibilityOff, Delete } from '@mui/icons-material';
import './VideoTutorials.scss';
import './Help.scss';
import AbsModal from '../../common/absModal/AbsModal';
import { ClaimsContext } from '../../services/ClaimsService';
import BreadCrumb from '../../common/breadCrumb/BreadCrumb';
import { HELP_DATA } from './constants';
import { useNavigate } from 'react-router-dom';
import { Button, COLORS, Modal } from 'abs-ui-components';
import axiosHttp from '../../services/interceptor';
import { emitToasterMsg } from '../../services/toasterUtility';
import AutocompleteDropdown from '../../common/autocompleteDropdown/AutocompleteDropdown';
import { useSelector } from 'react-redux';
import { isErSuperAdmin } from '../../utils/roleUtils';
import VimeoPlayer from './VimeoPlayer';

const PRIMARY_BUTTON_COLOR = '#002A4E';

const toVimeoEmbed = (url) => {
  try {
    if (!url) return null;
    
    const urlStr = String(url).trim();
    
    if (!urlStr) return null;
    
    // If it's already a player.vimeo.com embed URL, validate and return
    if (urlStr.includes('player.vimeo.com/video/')) {
      const existingMatch = urlStr.match(/player\.vimeo\.com\/video\/(\d{7,12})(?:\?h=([a-zA-Z0-9]+))?/);
      if (existingMatch) {
        const videoId = existingMatch[1];
        const hash = existingMatch[2];
        return hash 
          ? `https://player.vimeo.com/video/${videoId}?h=${hash}`
          : `https://player.vimeo.com/video/${videoId}`;
      }
      // Has player.vimeo.com but no valid video ID pattern
      return null;
    }
    
    // Try to extract video ID from various URL patterns
    const videoPatterns = [
      /\/video[=/](\d{7,12})/,
      /vimeo\.com\/(\d{7,12})(?:\/|$|\?)/,
      /\/(\d{7,12})(?:\/([a-zA-Z0-9]+))?$/,
    ];
    
    for (const pattern of videoPatterns) {
      const match = urlStr.match(pattern);
      if (match) {
        const videoId = match[1];
        const possibleHash = match[2];
        
        // Also check for h= parameter in query string
        const hParamMatch = urlStr.match(/[?&]h=([a-zA-Z0-9]+)/);
        const hash = hParamMatch ? hParamMatch[1] : possibleHash;
        
        if (hash && /^[a-zA-Z0-9]+$/.test(hash) && hash.length > 5) {
          return `https://player.vimeo.com/video/${videoId}?h=${hash}`;
        }
        
        return `https://player.vimeo.com/video/${videoId}`;
      }
    }
    
    // Fallback: extract the last sequence of 7-12 digits
    const matches = urlStr.match(/\d{7,12}/g);
    
    if (matches && matches.length > 0) {
      // Use the last match as it's most likely the video ID
      const videoId = matches[matches.length - 1];
      
      // Try to extract hash parameter if present (for private videos)
      const hashMatch = urlStr.match(new RegExp(`${videoId}[/\\?]([a-zA-Z0-9]+)`));
      const hash = hashMatch ? hashMatch[1] : null;
      
      // Also check for h= parameter in query string
      const hParamMatch = urlStr.match(/[?&]h=([a-zA-Z0-9]+)/);
      const hParam = hParamMatch ? hParamMatch[1] : hash;
      
      if (hParam && /^[a-zA-Z0-9]+$/.test(hParam) && hParam.length > 5) {
        return `https://player.vimeo.com/video/${videoId}?h=${hParam}`;
      }
      
      return `https://player.vimeo.com/video/${videoId}`;
    }
    
    // Fallback: if input is just a number (7-12 digits), treat it as video ID
    if (/^\d{7,12}$/.test(urlStr)) {
      return `https://player.vimeo.com/video/${urlStr}`;
    }
    
    // Could not extract a valid Vimeo video ID
    return null;
  } catch {
    // Last resort fallback
    const numMatch = String(url || '').match(/\d{7,12}/);
    if (numMatch) {
      return `https://player.vimeo.com/video/${numMatch[0]}`;
    }
    return null;
  }
};

/* ------------------------------- Component ------------------------------- */
const VideoTutorials = () => {
  /* --------------------------- Context & Routing -------------------------- */
  const navigate = useNavigate();
  const user = useSelector((state) => state.userInfo.userData);
  const isSuperAdmin = useMemo(() => isErSuperAdmin(user), [user]);

  /* --------------------------- State -------------------------- */
  const [groups, setGroups] = useState([]);
  const [selectedVideo, setSelectedVideo] = useState(null);

  const [loading, setLoading] = useState(false);

  const [searchTerm, setSearchTerm] = useState('');
  const [initialExpandedId, setInitialExpandedId] = useState(null);

  const [uploadOpen, setUploadOpen] = useState(false);
  const [newTitle, setNewTitle] = useState('');
  const [newDescription, setNewDescription] = useState('');
  const [newUrl, setNewUrl] = useState('');
  const [newGroupId, setNewGroupId] = useState('');
  const [editMode, setEditMode] = useState(false);
  const [editingRef, setEditingRef] = useState({ groupId: null, videoId: null });
  const [creatingVideo, setCreatingVideo] = useState(false);

  const [deleteGroupOpen, setDeleteGroupOpen] = useState(false);
  const [groupToDelete, setGroupToDelete] = useState(null);
  const [sectionToDelete, setSectionToDelete] = useState({ id: null, title: '' });
  const [editGroupOpen, setEditGroupOpen] = useState(false);
  const [groupToEdit, setGroupToEdit] = useState(null);
  const [editGroupTitle, setEditGroupTitle] = useState('');
  const [editingSection, setEditingSection] = useState(false);
  const [deletingSection, setDeletingSection] = useState(false);

  const [createGroupOpen, setCreateGroupOpen] = useState(false);
  const [newGroupTitle, setNewGroupTitle] = useState('');
  const [creatingSection, setCreatingSection] = useState(false);

  const [deleteVideoOpen, setDeleteVideoOpen] = useState(false);
  const [videoToDelete, setVideoToDelete] = useState({ groupId: null, videoId: null, title: '' });
  const [deletingVideo, setDeletingVideo] = useState(false);

  const [visibilityLoading, setVisibilityLoading] = useState(() => new Set());

  const [reorderOpen, setReorderOpen] = useState(false);
  const [reorderData, setReorderData] = useState([]);
  const [savingOrder, setSavingOrder] = useState(false);

  const [dragSectionIndex, setDragSectionIndex] = useState(null);
  const [dragVideoCtx, setDragVideoCtx] = useState({ sectionIndex: null, videoIndex: null });

  /* --------------------------- Feature Toggle -------------------------- */
      const location = useLocation();
      const wcnNumber = location.state?.wcnNumber;
  
      const { userHasFeature, userFeaturesAreLoaded, fetchUserFeatures, checkWcnDelta } =
      useContext(ClaimsContext);
  
      useEffect(() => {
          if (!userFeaturesAreLoaded || checkWcnDelta(wcnNumber)) {
            fetchUserFeatures(wcnNumber);
          }
        }, [userFeaturesAreLoaded, fetchUserFeatures, checkWcnDelta, wcnNumber]);
  
      const canShowVideo = useMemo(() => {
          return userHasFeature(FeatureType.ViewSupportVideoTutorials);
      }, [userHasFeature]);
      const canCreateVideo = useMemo(() => {
          return isSuperAdmin && userHasFeature(FeatureType.CreateSupportVideoTutorial);
      }, [isSuperAdmin, userHasFeature]);
      const canEditVideo = useMemo(() => {
          return isSuperAdmin && userHasFeature(FeatureType.EditSupportVideoTutorial);
      }, [isSuperAdmin, userHasFeature]);
      const canDeleteVideo = useMemo(() => {
          return isSuperAdmin && userHasFeature(FeatureType.DeleteSupportVideoTutorial);
      }, [isSuperAdmin, userHasFeature]);
      const canCreateSection = useMemo(() => {
          return isSuperAdmin && userHasFeature(FeatureType.CreateSupportVideoTutorialSection);
      }, [isSuperAdmin, userHasFeature]);
      const canEditSection = useMemo(() => {
          return isSuperAdmin && userHasFeature(FeatureType.EditSupportVideoTutorialSection);
      }, [isSuperAdmin, userHasFeature]);
      const canDeleteSection = useMemo(() => {
          return isSuperAdmin && userHasFeature(FeatureType.DeleteSupportVideoTutorialSection);
      }, [isSuperAdmin, userHasFeature]);

  /* --------------------------- Derived Data -------------------------- */
  const breadcrumb = useMemo(
    () => [
      { label: HELP_DATA['nav-support-text'], url: '/support', navId: 'support_navId' },
      { label: HELP_DATA['nav-help-text'], url: '/support', navId: 'help_navId' },
      { label: 'Video Tutorials', url: '', navId: 'video_tutorials_navId' },
    ],
    []
  );

  const filteredGroups = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();

    // No filtering when search is empty
    if (!q) {
      return groups.map((g) => ({
        ...g,
        // apply visibility rule only (admins see all)
        items: g.items.filter((v) => (isSuperAdmin ? true : !v.hidden)),
      }));
    }

    // Filter by video title only; hide sections with no matches
    return groups
      .map((g) => {
        const visibleItems = g.items.filter((v) => (isSuperAdmin ? true : !v.hidden));
        const itemsByTitle = visibleItems.filter((v) => v.title.toLowerCase().includes(q));
        return { ...g, items: itemsByTitle };
      })
      .filter((g) => g.items.length > 0);
  }, [groups, searchTerm, isSuperAdmin]);

  const selectedGroup = useMemo(() => {
    if (!groups?.length || !selectedVideo) return { id: null, title: '', items: [] };
    return (
      groups.find((g) => g.items.some((i) => i.id === selectedVideo?.id)) ??
      groups[0] ??
      { id: null, title: '', items: [] }
    );
  }, [groups, selectedVideo]);

  /* --------------------------- API Calls & Callbacks -------------------------- */
  const fetchTutorials = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axiosHttp.get('api/Support/GetVideoTutorials');

      const payload = Array.isArray(response?.data)
        ? response.data
        : Array.isArray(response?.data?.Data)
        ? response.data.Data
        : [];

      // Order sections by position (supports camelCase/PascalCase)
      const orderedSections = [...payload].sort((a, b) => {
        const pa = a.position ?? a.Position ?? Number.MAX_SAFE_INTEGER;
        const pb = b.position ?? b.Position ?? Number.MAX_SAFE_INTEGER;
        return pa - pb;
      });

      const mapped = orderedSections.map((section) => {
        const sectionId = section.sectionId ?? section.SectionId;
        const sectionName = section.sectionName ?? section.SectionName;
        const details = Array.isArray(section.videoDetails ?? section.VideoDetails)
          ? (section.videoDetails ?? section.VideoDetails)
          : [];

        // Order videos by position first
        const orderedDetails = [...details].sort((a, b) => {
          const pa = a.position ?? a.Position ?? Number.MAX_SAFE_INTEGER;
          const pb = b.position ?? b.Position ?? Number.MAX_SAFE_INTEGER;
          return pa - pb;
        });

        // Only Vimeo videos; normalize to embed URLs
        const vimeoItems = orderedDetails
          .filter((v) => {
            const url = v.url ?? v.Url ?? '';
            try {
              const u = new URL(url);
              return u.hostname.includes('vimeo.com');
            } catch {
              return /^\d+$/.test(String(url));
            }
          })
          .map((v) => ({
            id: String(v.videoId ?? v.VideoId),
            title: v.title ?? v.Title,
            description: v.description ?? v.Description,
            url: toVimeoEmbed(v.url ?? v.Url),
            hidden:
              typeof (v.isViewable ?? v.IsViewable) === 'boolean'
                ? !(v.isViewable ?? v.IsViewable)
                : false,
          }));

        return {
          id: String(sectionId),
          title: sectionName,
          items: vimeoItems,
        };
      });

      setGroups(mapped);

      // Do not autoexpand any section or auto select a video on load
      setInitialExpandedId(null);
      setSelectedVideo(null);
    } catch (err) {
      console.error('Failed to load video tutorials', err);
      setGroups([]);
      setSelectedVideo(null);
    } finally {
      setLoading(false);
    }
  }, []);

  const onToggleVisibility = async (groupId, video) => {
    const videoId = video?.id;
    if (!videoId) return;

    const nextIsViewable = video.hidden ? true : false;

    setVisibilityLoading((prev) => {
      const next = new Set(prev);
      next.add(String(videoId));
      return next;
    });

    try {
      const response = await axiosHttp.post(
        `api/Support/ToggleVideoTutorialVisibility?videoId=${encodeURIComponent(videoId)}`,
        { isViewable: nextIsViewable }
      );

      if (response?.data?.isSuccess === true) {
        // Update local state to reflect new visibility
        setGroups((prev) =>
          prev.map((g) =>
            String(g.id) === String(groupId)
              ? {
                  ...g,
                  items: g.items.map((v) =>
                    String(v.id) === String(videoId) ? { ...v, hidden: !v.hidden } : v
                  ),
                }
              : g
          )
        );
      } else {
        emitToasterMsg('error', 'Failed to update visibility.');
      }
    } catch (err) {
      console.error('Failed to update visibility', err);
      emitToasterMsg('error', 'Failed to update visibility.');
    } finally {
      setVisibilityLoading((prev) => {
        const next = new Set(prev);
        next.delete(String(videoId));
        return next;
      });
    }
  };

  const handleSaveLink = async () => {
    const title = newTitle.trim();
    const description = newDescription.trim();
    const url = newUrl.trim();
    if (!title || !url || !newGroupId) return;

    const embed = toVimeoEmbed(url);

    if (!embed) {
      emitToasterMsg(
        'error',
        'Unable to extract a valid Vimeo video ID. Please provide a valid Vimeo URL (e.g., https://vimeo.com/123456789).'
      );
      return;
    }

    if (editMode && editingRef.videoId && editingRef.groupId) {
      try {
        setCreatingVideo(true);

        const response = await axiosHttp.post(
          `api/Support/EditVideoTutorial?videoId=${encodeURIComponent(editingRef.videoId)}`,
          {
            SectionId: Number(newGroupId),
            Title: title,
            Description: description,
            Url: embed,
          }
        );

        if (response?.data?.isSuccess === true) {
          emitToasterMsg('success', 'Video updated successfully.');
          setUploadOpen(false);
          resetUploadForm();
          await fetchTutorials();
          setSelectedVideo((prev) => {
            const all = typeof groups?.flatMap === 'function' ? groups.flatMap((g) => g.items) : [];
            return (
              all.find((v) => String(v.id) === String(editingRef.videoId)) ||
              all.find((v) => v.title === title && (v.url === embed || v.url === url)) ||
              prev
            );
          });
        } else {
          emitToasterMsg('error', 'Failed to update video.');
        }
      } catch (err) {
        console.error('Failed to update video', err);
        emitToasterMsg('error', 'Failed to update video.');
      } finally {
        setCreatingVideo(false);
      }
      return;
    }

    // Create video via API
    try {
      setCreatingVideo(true);

      const response = await axiosHttp.post('api/Support/CreateVideoTutorial', {
        SectionId: Number(newGroupId),
        Title: title,
        Description: description,
        Url: embed,
      });

      if (response?.data?.isSuccess === true) {
        emitToasterMsg('success', 'Video uploaded successfully.');
        setUploadOpen(false);
        resetUploadForm();
        await fetchTutorials();
        setSelectedVideo((prev) => {
          const all = typeof groups?.flatMap === 'function' ? groups.flatMap((g) => g.items) : [];
          return all.find((v) => v.title === title && (v.url === embed || v.url === url)) || prev;
        });
      } else {
        emitToasterMsg('error', 'Failed to upload video.');
      }
    } catch (err) {
      console.error('Failed to upload video', err);
      emitToasterMsg('error', 'Failed to upload video.');
    } finally {
      setCreatingVideo(false);
    }
  };

  const confirmDeleteVideo = async () => {
    const { videoId } = videoToDelete || {};
    if (!videoId) return;

    try {
      setDeletingVideo(true);

      const response = await axiosHttp.delete(
        `api/Support/DeleteVideoTutorial?videoId=${encodeURIComponent(videoId)}`
      );

      if (response?.data?.isSuccess === true) {
        emitToasterMsg('success', 'Video deleted successfully.');
        setDeleteVideoOpen(false);
        setVideoToDelete({ groupId: null, videoId: null, title: '' });
        await fetchTutorials();
      } else {
        emitToasterMsg('error', 'Failed to delete video.');
      }
    } catch (err) {
      console.error('Failed to delete video', err);
      emitToasterMsg('error', 'Failed to delete video.');
    } finally {
      setDeletingVideo(false);
    }
  };

  const GetSectionsList = async (inputText, groups) => {
    try {
      const q = (inputText || '').trim().toLowerCase();
      const list = Array.isArray(groups) ? groups : [];
      const filtered = q
        ? list.filter((g) => (g?.title || '').toLowerCase().includes(q))
        : list;

      return filtered.map((g, index) => ({
        label: g.title,
        value: String(g.id),
        id: index + 1,
      }));
    } catch {
      return [];
    }
  };

  const requestEditGroup = (group) => {
    setGroupToEdit(group);
    setEditGroupTitle(group.title);
    setEditGroupOpen(true);
  };

  const saveEditGroup = async () => {
    const title = editGroupTitle.trim();
    if (!groupToEdit || !title) return;

    try {
      setEditingSection(true);

      const response = await axiosHttp.post(
        `api/Support/EditVideoTutorialSection?sectionId=${encodeURIComponent(groupToEdit.id)}`,
        { SectionName: title }
      );

      if (response?.data?.isSuccess === true) {
        emitToasterMsg('success', 'Section updated successfully.');
        setEditGroupOpen(false);
        setGroupToEdit(null);
        setEditGroupTitle('');
        await fetchTutorials();
      } else {
        emitToasterMsg('error', 'Failed to update section.');
      }
    } catch (err) {
      console.error('Failed to update section', err);
      emitToasterMsg('error', 'Failed to update section.');
    } finally {
      setEditingSection(false);
    }
  };

  const requestDeleteGroup = (group) => {
    setGroupToDelete(group);
    setSectionToDelete({ id: group.id, title: group.title });
    setDeleteGroupOpen(true);
  };

  const confirmDeleteGroup = async () => {
    if (!groupToDelete) return;
    try {
      setDeletingSection(true);

      const response = await axiosHttp.delete(
        `api/Support/DeleteVideoTutorialSection?sectionId=${encodeURIComponent(groupToDelete.id)}`
      );

      if (response.data.isSuccess === true) {
        emitToasterMsg('success', 'Section deleted successfully.');
        setDeleteGroupOpen(false);
        setGroupToDelete(null);
        setSectionToDelete({ id: null, title: '' });
        await fetchTutorials();
      } else {
        emitToasterMsg('error', 'Failed to delete section.');
      }
    } catch (err) {
      console.error('Failed to delete section', err);
      emitToasterMsg('error', 'Failed to delete section.');
    } finally {
      setDeletingSection(false);
    }
  };

  const createNewSection = async () => {
    const title = newGroupTitle.trim();
    if (!title) return;

    try {
      setCreatingSection(true);

      const response = await axiosHttp.post('api/Support/CreateVideoTutorialSection', {
        SectionName: title,
      });

      if (response?.data?.isSuccess === true) {
        emitToasterMsg('success', 'Section created successfully.');
        setCreateGroupOpen(false);
        setNewGroupTitle('');
        await fetchTutorials();
      } else {
        emitToasterMsg('error', 'Failed to create section.');
      }
    } catch (err) {
      console.error('Failed to create section', err);
      emitToasterMsg('error', 'Failed to create section.');
    } finally {
      setCreatingSection(false);
    }
  };

  const saveReorder = async () => {
    const payload = reorderData.map((section, sIdx) => ({
      SectionId: Number(section.id),
      SectionName: section.title || '',
      Position: sIdx + 1,
      VideoDetails: section.items.map((v, vIdx) => ({
        VideoId: Number(v.id),
        Title: v.title || '',
        Description: v.description,
        Url: v.url || '',
        IsViewable: Boolean(v.hidden),
        Position: vIdx + 1,
      })),
    }));

    try {
      setSavingOrder(true);
      const response = await axiosHttp.post('api/Support/SaveVideoTutorialPosition', payload);
      if (response?.data?.isSuccess === true) {
        emitToasterMsg('success', 'Order saved successfully.');
        setReorderOpen(false);
        setReorderData([]);
        await fetchTutorials();
      } else {
        emitToasterMsg('error', 'Failed to save order.');
      }
    } catch (err) {
      console.error('Failed to save order', err);
      emitToasterMsg('error', 'Failed to save order.');
    } finally {
      setSavingOrder(false);
    }
  };

  /* --------------------------- Effects -------------------------- */
  useEffect(() => {
    (async () => {
      await fetchTutorials();
    })();
  }, [fetchTutorials]);

  useEffect(() => {
    if (!newGroupId && groups?.[0]?.id) {
      setNewGroupId(groups[0].id);
    }
  }, [groups, newGroupId]);

  /* --------------------------- Handlers & Local Utilities -------------------------- */
  const handleVideoSelect = (video) => setSelectedVideo(video);

  const handlePrev = () => {
    if (!selectedVideo || !selectedGroup?.items?.length) return;
    const currentIndex = selectedGroup.items.findIndex((v) => v.id === selectedVideo.id);
    if (currentIndex > 0) setSelectedVideo(selectedGroup.items[currentIndex - 1]);
  };

  const handleNext = () => {
    if (!selectedVideo || !selectedGroup?.items?.length) return;
    const currentIndex = selectedGroup.items.findIndex((v) => v.id === selectedVideo.id);
    if (currentIndex < selectedGroup.items.length - 1)
      setSelectedVideo(selectedGroup.items[currentIndex + 1]);
  };

  const resetUploadForm = () => {
    setNewTitle('');
    setNewDescription('');
    setNewUrl('');
    setNewGroupId(groups?.[0]?.id || '');
    setEditMode(false);
    setEditingRef({ groupId: null, videoId: null });
  };

  const onEdit = (groupId, video) => {
    setEditMode(true);
    setEditingRef({ groupId, videoId: video.id });
    setNewTitle(video.title);
    setNewDescription(video.description);
    setNewUrl(video.url);
    setNewGroupId(String(groupId));
    setUploadOpen(true);
  };

  const onDelete = (groupId, videoId) => {
    const video = groups
      .find((g) => String(g.id) === String(groupId))
      ?.items.find((v) => String(v.id) === String(videoId));
    setVideoToDelete({ groupId, videoId, title: video?.title ?? '' });
    setDeleteVideoOpen(true);
  };

  const cancelDeleteVideo = () => {
    if (deletingVideo) return;
    setDeleteVideoOpen(false);
    setVideoToDelete({ groupId: null, videoId: null, title: '' });
  };

  const openReorderModal = () => {
    const clone = groups.map((g, gi) => ({
      id: g.id,
      title: g.title,
      position: gi,
      items: g.items.map((v, vi) => ({
        id: v.id,
        title: v.title,
        description: v.description,
        url: v.url,
        isViewable: !v.hidden,
        position: vi,
      })),
    }));
    setReorderData(clone);
    setReorderOpen(true);
  };

  const onSectionDragStart = (e, sectionIndex) => {
    setDragSectionIndex(sectionIndex);
    e.dataTransfer.effectAllowed = 'move';
  };
  const onSectionDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };
  const onSectionDrop = (e, dropIndex) => {
    e.preventDefault();
    if (dragSectionIndex === null || dragSectionIndex === dropIndex) return;
    setReorderData((prev) => {
      const next = [...prev];
      const [moved] = next.splice(dragSectionIndex, 1);
      next.splice(dropIndex, 0, moved);
      return next;
    });
    setDragSectionIndex(null);
  };
  const onSectionDragEnd = () => setDragSectionIndex(null);

  const onVideoDragStart = (e, sectionIndex, videoIndex) => {
    setDragVideoCtx({ sectionIndex, videoIndex });
    e.dataTransfer.effectAllowed = 'move';
  };
  const onVideoDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };
  const onVideoDrop = (e, sectionIndex, dropVideoIndex) => {
    e.preventDefault();
    const { sectionIndex: fromSection, videoIndex: fromVideo } = dragVideoCtx;
    if (fromSection === null || fromVideo === null) return;
    if (fromSection !== sectionIndex || fromVideo === dropVideoIndex) {
      setDragVideoCtx({ sectionIndex: null, videoIndex: null });
      return;
    }
    setReorderData((prev) => {
      const next = [...prev];
      const videos = [...next[sectionIndex].items];
      const [moved] = videos.splice(fromVideo, 1);
      videos.splice(dropVideoIndex, 0, moved);
      next[sectionIndex] = { ...next[sectionIndex], items: videos };
      return next;
    });
    setDragVideoCtx({ sectionIndex: null, videoIndex: null });
  };
  const onVideoDragEnd = () => setDragVideoCtx({ sectionIndex: null, videoIndex: null });

  /* --------------------------- JSX -------------------------- */
  if (!canShowVideo) {
    return (
      <MDBContainer fluid className="px-2 px-md-3 py-4">
        <div className="mb-3">
          <BreadCrumb
            items={breadcrumb}
            onCrumbClick={(item) => {
              if (item.url) navigate(item.url);
            }}
          />
        </div>
        <MDBRow className="gx-2">
          <MDBCol md="12" lg="12">
            <MDBCard className="h-100">
              <MDBCardBody>
                <MDBCardTitle className="mb-0">Video Tutorials</MDBCardTitle>
                <div className="mt-3">You don’t have permission to view video tutorials.</div>
              </MDBCardBody>
            </MDBCard>
          </MDBCol>
        </MDBRow>
      </MDBContainer>
    );
  }

  return (
    <MDBContainer fluid className="px-2 px-md-3 py-4">
      {/* Breadcrumb navigation */}
      <div className="mb-3">
        <BreadCrumb
          items={breadcrumb}
          onCrumbClick={(item) => {
            if (item.url) navigate(item.url);
          }}
        />
      </div>

      {/* Page layout: left list + right player */}
      <MDBRow className="gx-2">
        {/* Left column: tutorials list, search, actions, accordion */}
        <MDBCol md="4" lg="4">
          <MDBCard className="h-100">
            <MDBCardBody>
              {/* List header: title + actions (upload/create section) */}
              <div className="d-flex align-items-center justify-content-between">
                <MDBCardTitle className="mb-0">Tutorials List</MDBCardTitle>
                <div className="d-flex align-items-center flex-wrap justify-content-end video-tutorials-actions">
                  {canCreateVideo && (
                    <Button
                      className="tutorials-list-header-btn"
                      onClick={() => setUploadOpen(true)}
                      disabled={loading || groups.length === 0}
                    >
                      Upload Video
                    </Button>
                  )}
                  {canCreateSection && (
                    <Button
                      className="tutorials-list-header-btn"
                      onClick={() => setCreateGroupOpen(true)}
                      disabled={loading}
                    >
                      Create New Section
                    </Button>
                  )}
                </div>
              </div>

              {/* Search bar */}
              <div className="mb-3 mt-3">
                <MDBInput
                  label="Search tutorials"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  size="sm"
                  disabled={loading}
                />
              </div>

              {/* Reorder button */}
              <div className="video-tutorials-reorder">
                {canEditSection && (
                  <Button
                    className="tutorials-list-header-btn"
                    onClick={openReorderModal}
                    disabled={loading || groups.length === 0}
                  >
                    Reorder
                  </Button>
                )}
              </div>

              {/* Accordion with sections and videos */}
              <div className="tutorials-list">
                <MDBAccordion
                  key={initialExpandedId || 'accordion'}
                  flush
                  initialActive={initialExpandedId || null}
                  alwaysOpen={false}
                >
                  {loading ? (
                    <div className="d-flex justify-content-center align-items-center py-4">
                      <span
                        className="spinner-border spinner-border-sm"
                        role="status"
                        aria-label="Loading tutorials"
                      />
                    </div>
                  ) : filteredGroups.length === 0 ? (
                    <div className="px-2 py-2 text-muted">No tutorials found.</div>
                  ) : (
                    filteredGroups.map((group) => (
                      <MDBAccordionItem
                        key={group.id}
                        collapseId={group.id}
                        headerTitle={
                          // Section header: title + actions
                          <div className="d-flex align-items-center justify-content-between w-100">
                            <span>{group.title}</span>
                            {(canEditSection || canDeleteSection) && (
                              <span className="section-header-actions">
                                {canEditSection && (
                                  <MDBBtn
                                    size="sm"
                                    color="link"
                                    className="p-0 action-icon-btn"
                                    ripple={false}
                                    title="Edit section"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      requestEditGroup(group);
                                    }}
                                  >
                                    <EditIcon fontSize="small" sx={{ color: 'white' }} />
                                  </MDBBtn>
                                )}
                                {canDeleteSection && (
                                  <MDBBtn
                                    size="sm"
                                    color="link"
                                    className="p-0 ms-2 text-danger action-icon-btn"
                                    ripple={false}
                                    title="Delete section"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      requestDeleteGroup(group);
                                    }}
                                  >
                                    <Delete fontSize="small" />
                                  </MDBBtn>
                                )}
                              </span>
                            )}
                          </div>
                        }
                      >
                        {/* Section body: videos list */}
                        <MDBListGroup flush>
                          {group.items.length === 0 ? (
                            <MDBListGroupItem className="text-muted">
                              No videos in this section
                            </MDBListGroupItem>
                          ) : (
                            group.items.map((video) => {
                              const isHidden = groups
                                .find((g) => String(g.id) === String(group.id))
                                ?.items.find((i) => String(i.id) === String(video.id))?.hidden;
                              return (
                                <MDBListGroupItem
                                  key={video.id}
                                  onClick={() => handleVideoSelect(video)}
                                  action
                                  active={selectedVideo?.id === video.id}
                                  className={`cursor-pointer ${
                                    selectedVideo?.id === video.id ? 'bg-custom' : 'bg-black'
                                  } ${isHidden ? 'video-item-hidden' : ''}`}
                                >
                                  {/* Video item row: title + actions */}
                                  <div className="d-flex align-items-center">
                                    <div className="flex-grow-1">
                                      <div>{video.title}</div>
                                      {video.description && (
                                        <small className="video-item-description d-block text-muted">
                                          {video.description}
                                        </small>
                                      )}
                                    </div>
                                    {(canEditVideo || canDeleteVideo) && (
                                      <span className="video-item-actions">
                                        {/* Edit video */}
                                        {canEditVideo && (
                                          <MDBBtn
                                            size="sm"
                                            color="link"
                                            className="p-0 action-icon-btn"
                                            ripple={false}
                                            title="Edit"
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              onEdit(group.id, video);
                                            }}
                                          >
                                            <EditIcon fontSize="small" sx={{ color: '#6c757d' }} />
                                          </MDBBtn>
                                        )}

                                        {/* Toggle visibility */}
                                        {canEditVideo && (
                                          <MDBBtn
                                            size="sm"
                                            color="link"
                                            className="p-0 ms-2 action-icon-btn"
                                            ripple={false}
                                            title={isHidden ? 'Show' : 'Hide'}
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              onToggleVisibility(group.id, video);
                                            }}
                                            disabled={visibilityLoading.has(String(video.id))}
                                          >
                                            {visibilityLoading.has(String(video.id)) ? (
                                              <span
                                                className="spinner-border spinner-border-sm"
                                                role="status"
                                                aria-hidden="true"
                                              />
                                            ) : isHidden ? (
                                              <VisibilityOff fontSize="small" sx={{ color: '#6c757d' }} />
                                            ) : (
                                              <Visibility fontSize="small" sx={{ color: '#002a4e' }} />
                                            )}
                                          </MDBBtn>
                                        )}

                                        {/* Delete video */}
                                        {canDeleteVideo && (
                                          <MDBBtn
                                            size="sm"
                                            color="link"
                                            className="p-0 ms-2 text-danger action-icon-btn"
                                            ripple={false}
                                            title="Delete"
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              onDelete(group.id, video.id);
                                            }}
                                          >
                                            <Delete fontSize="small" />
                                          </MDBBtn>
                                        )}
                                      </span>
                                    )}
                                  </div>
                                </MDBListGroupItem>
                              );
                            })
                          )}
                        </MDBListGroup>
                      </MDBAccordionItem>
                    ))
                  )}
                </MDBAccordion>
              </div>
            </MDBCardBody>
          </MDBCard>
        </MDBCol>

        {/* Right column: player and navigation */}
        <MDBCol md="8">
          <MDBCard className="h-100">
            <MDBCardBody className="d-flex flex-column">
              {/* Selected video player or placeholder */}
              {selectedVideo ? (
                <>
                  <MDBCardTitle>{selectedVideo.title}</MDBCardTitle>
                  <div className="video-embed mb-3">
                    <VimeoPlayer
                      videoKey={selectedVideo.id}
                      src={selectedVideo.url}
                      title={selectedVideo.title}
                    />
                  </div>
                </>
              ) : (
                <div
                  className="video-embed video-embed-placeholder mb-3 d-flex align-items-center justify-content-center bg-black text-white w-100 flex-grow-1"
                  style={{ minHeight: '400px' }}
                >
                  <span className="text-center px-3">Please select a video</span>
                </div>
              )}

              {/* Prev/Next navigation buttons */}
              <div className="d-flex justify-content-between help-page-btn">
                <MDBBtn
                  onClick={handlePrev}
                  disabled={!selectedVideo || selectedVideo.id === selectedGroup?.items?.[0]?.id}
                  className="custom-button"
                >
                  Prev
                </MDBBtn>
                <MDBBtn
                  onClick={handleNext}
                  disabled={
                    !selectedVideo ||
                    selectedVideo.id === selectedGroup?.items?.[selectedGroup?.items?.length - 1]?.id
                  }
                  className="custom-button"
                >
                  Next
                </MDBBtn>
              </div>
            </MDBCardBody>
          </MDBCard>
        </MDBCol>
      </MDBRow>

      {/* Upload/Edit video modal */}
      {canCreateVideo || canEditVideo ? (
        <AbsModal
          isOpen={uploadOpen}
          title={editMode ? 'Edit tutorial video' : 'Add tutorial video'}
          onClose={setUploadOpen}
          close={() => {
            setUploadOpen(false);
            resetUploadForm();
          }}
        >
          <div className="video-upload-modal-body p-3">
            {/* Form fields: title and Vimeo URL */}
            <div className="mb-3">
              <MDBInput
                label="Title"
                value={newTitle}
                onChange={(e) => setNewTitle(e.target.value)}
                disabled={creatingVideo}
              />
            </div>
            <div className="mb-3">
              <MDBTextArea
                label="Description"
                value={newDescription}
                required={false}
                onChange={(e) => setNewDescription(e.target.value)}
                disabled={creatingVideo}
              />
            </div>
            <div className="mb-3">
              <MDBInput
                label="Vimeo URL"
                value={newUrl}
                onChange={(e) => setNewUrl(e.target.value)}
                placeholder="https://player.vimeo.com/video/123456789"
                disabled={creatingVideo}
              />
            </div>

            {/* Section selector */}
            <div className="mb-4">
              <label className="form-label">Section</label>
              <AutocompleteDropdown
                className="inputHeight"
                id="video-section-select"
                isClearable={false}
                loadOptions={async (inputText) => {
                  const options = await GetSectionsList(inputText, groups);
                  return options;
                }}
                placeholder={
                  groups.find((g) => String(g.id) === String(newGroupId))?.title || 'Select a section'
                }
                autoFocus={false}
                onChange={(selected) => {
                  setNewGroupId(selected?.value || groups?.[0]?.id || '');
                }}
              />
            </div>

            {/* Modal actions: Add then Cancel (match app styling) */}
            <div className="help-modal-footer">
              <Button
                className="save-button"
                onClick={handleSaveLink}
                disabled={creatingVideo || !newTitle.trim() || !newUrl.trim() || !newGroupId}
                primaryBackgroundColor={PRIMARY_BUTTON_COLOR}
                primaryColor={PRIMARY_BUTTON_COLOR}
              >
                <span className="d-inline-flex align-items-center">
                  {creatingVideo && (
                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
                  )}
                  {editMode ? 'Save' : 'Add'}
                </span>
              </Button>
              <Button
                className="clear-button"
                onClick={() => {
                  setUploadOpen(false);
                  resetUploadForm();
                }}
                disabled={creatingVideo}
              >
                Cancel
              </Button>
            </div>
          </div>
        </AbsModal>
      ) : null}

      {/* Edit section modal */}
      {canEditSection && (
        <AbsModal
          isOpen={editGroupOpen}
          title="Edit section"
          onClose={setEditGroupOpen}
          close={() => {
            setEditGroupOpen(false);
            setGroupToEdit(null);
            setEditGroupTitle('');
          }}
        >
          {/* Form field: section title */}
          <div className="mb-3">
            <MDBInput
              label="Section title"
              value={editGroupTitle}
              onChange={(e) => setEditGroupTitle(e.target.value)}
              disabled={editingSection}
            />
          </div>

          {/* Modal actions: Save then Cancel (match app styling) */}
          <div className="help-modal-footer">
            <Button
              className="save-button"
              onClick={saveEditGroup}
              disabled={editingSection || !editGroupTitle.trim()}
              primaryBackgroundColor={PRIMARY_BUTTON_COLOR}
              primaryColor={PRIMARY_BUTTON_COLOR}
            >
              {editingSection ? (
                <span className="d-inline-flex align-items-center">
                  <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
                  Saving...
                </span>
              ) : (
                'Save'
              )}
            </Button>
            <Button
              className="clear-button"
              onClick={() => {
                setEditGroupOpen(false);
                setGroupToEdit(null);
                setEditGroupTitle('');
              }}
              disabled={editingSection}
            >
              Cancel
            </Button>
          </div>
        </AbsModal>
      )}

      {/* Create section modal */}
      {canCreateSection && (
        <AbsModal
          isOpen={createGroupOpen}
          title="Create new section"
          onClose={setCreateGroupOpen}
          close={() => {
            setCreateGroupOpen(false);
            setNewGroupTitle('');
          }}
        >
          {/* Form field: new section title */}
          <div className="mb-3">
            <MDBInput
              label="Section title"
              value={newGroupTitle}
              onChange={(e) => setNewGroupTitle(e.target.value)}
              disabled={creatingSection}
            />
          </div>

          {/* Modal actions: Create then Cancel (match app styling) */}
          <div className="help-modal-footer">
            <Button
              className="save-button"
              onClick={createNewSection}
              disabled={creatingSection || !newGroupTitle.trim()}
              primaryBackgroundColor={PRIMARY_BUTTON_COLOR}
              primaryColor={PRIMARY_BUTTON_COLOR}
            >
              <span className="d-inline-flex align-items-center">
                {creatingSection && (
                  <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
                )}
                Create
              </span>
            </Button>
            <Button
              className="clear-button"
              onClick={() => {
                setCreateGroupOpen(false);
                setNewGroupTitle('');
              }}
              disabled={creatingSection}
            >
              Cancel
            </Button>
          </div>
        </AbsModal>
      )}

      {/* Delete section modal */}
      {canDeleteSection && (
        <div className="confirmation-modal-container">
          <Modal
            isOpen={deleteGroupOpen}
            modalHeader
            padding={'70px'}
            headerHeight={'50px'}
            showFooter
            onCloseClickListener={() => {
              if (deletingSection) return; // prevent closing while pending
              setDeleteGroupOpen(false);
              setGroupToDelete(null);
              setSectionToDelete({ id: null, title: '' });
            }}
            footer={
              // Modal footer actions: delete/cancel
              <div className="d-flex justify-content-end fixed-action-btns p-4">
                <Button
                  className="modal-delete-btn"
                  onClick={() => {
                    confirmDeleteGroup();
                  }}
                  primaryColor={COLORS.RED.DARK}
                  primaryBackgroundColor={COLORS.RED.LIGHT}
                  disabled={deletingSection}
                >
                  {deletingSection ? (
                    <span className="d-inline-flex align-items-center">
                      <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
                      Deleting...
                    </span>
                  ) : (
                    'Delete'
                  )}
                </Button>
                <Button
                  className="modal-cancel-btn"
                  onClick={() => {
                    if (deletingSection) return;
                    setDeleteGroupOpen(false);
                    setGroupToDelete(null);
                    setSectionToDelete({ id: null, title: '' });
                  }}
                  disabled={deletingSection}
                >
                  Cancel
                </Button>
              </div>
            }
          >
            {/* Modal body text */}
            <div>
              Are you sure you want to delete this section{sectionToDelete?.title ? `: "${sectionToDelete.title}"` : ''}?
              All videos under this section will be deleted as well.
            </div>
          </Modal>
        </div>
      )}

      {/* Delete video modal */}
      {canDeleteVideo && (
        <div className="confirmation-modal-container">
          <Modal
            isOpen={deleteVideoOpen}
            modalHeader
            padding={'70px'}
            headerHeight={'50px'}
            showFooter
            onCloseClickListener={cancelDeleteVideo}
            footer={
              // Modal footer actions: delete/cancel
              <div className="d-flex justify-content-end fixed-action-btns p-4">
                <Button
                  className="modal-delete-btn"
                  onClick={confirmDeleteVideo}
                  primaryColor={COLORS.RED.DARK}
                  primaryBackgroundColor={COLORS.RED.LIGHT}
                  disabled={deletingVideo}
                >
                  {deletingVideo ? (
                    <span className="d-inline-flex align-items-center">
                      <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
                      Deleting...
                    </span>
                  ) : (
                    'Delete'
                  )}
                </Button>
                <Button className="modal-cancel-btn" onClick={cancelDeleteVideo} disabled={deletingVideo}>
                  Cancel
                </Button>
              </div>
            }
          >
            {/* Modal body text */}
            <div>
              Are you sure you want to delete this video{videoToDelete?.title ? `: "${videoToDelete.title}"` : ''}?
            </div>
          </Modal>
        </div>
      )}

      {/* Reorder modal */}
      {canEditSection && (
        <AbsModal
          isOpen={reorderOpen}
          title="Reorder sections and videos"
          onClose={setReorderOpen}
          close={() => {
            if (savingOrder) return;
            setReorderOpen(false);
            setReorderData([]);
            setDragSectionIndex(null);
            setDragVideoCtx({ sectionIndex: null, videoIndex: null });
          }}
        >
          {/* Reorder modal content: draggable sections and videos */}
          <div className="reorder-modal-content">
            {reorderData.length === 0 ? (
              <div className="text-muted">No sections available.</div>
            ) : (
              <div className="reorder-sections">
                {reorderData.map((section, sIdx) => (
                  <div
                    key={section.id}
                    className={`reorder-section border rounded p-2 mb-3 ${dragSectionIndex === sIdx ? 'bg-light' : ''}`}
                    draggable={!savingOrder}
                    onDragStart={(e) => onSectionDragStart(e, sIdx)}
                    onDragOver={onSectionDragOver}
                    onDrop={(e) => onSectionDrop(e, sIdx)}
                    onDragEnd={onSectionDragEnd}
                  >
                    {/* Section header row within reorder modal */}
                    <div className="d-flex align-items-center mb-2">
                      <span className="me-2 cursor-grab">☰</span>
                      <strong className="flex-grow-1">{section.title}</strong>
                      <span className="text-muted">Section #{sIdx + 1}</span>
                      <span className="text-muted">Position: {section.position + 1}</span>
                    </div>

                    {/* Videos list within section (draggable) */}
                    <MDBListGroup flush>
                      {section.items.length === 0 ? (
                        <MDBListGroupItem className="text-muted">No videos in this section</MDBListGroupItem>
                      ) : (
                        section.items.map((video, vIdx) => (
                          <MDBListGroupItem
                            key={video.id}
                            className="d-flex align-items-center"
                            draggable={!savingOrder}
                            onDragStart={(e) => onVideoDragStart(e, sIdx, vIdx)}
                            onDragOver={onVideoDragOver}
                            onDrop={(e) => onVideoDrop(e, sIdx, vIdx)}
                            onDragEnd={onVideoDragEnd}
                          >
                            <span className="me-2 cursor-grab">⋮⋮</span>
                            <span className="flex-grow-1">{video.title}</span>
                            <span className="badge bg-secondary ms-2">#{vIdx + 1}</span>
                            {!video.isViewable && <span className="badge bg-dark ms-2">Hidden</span>}
                          </MDBListGroupItem>
                        ))
                      )}
                    </MDBListGroup>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Reorder modal actions: Save then Cancel (match app styling) */}
          <div className="help-modal-footer mt-3">
            <Button
              className="save-button"
              onClick={saveReorder}
              disabled={savingOrder || reorderData.length === 0}
              primaryBackgroundColor={PRIMARY_BUTTON_COLOR}
              primaryColor={PRIMARY_BUTTON_COLOR}
            >
              <span className="d-inline-flex align-items-center">
                {savingOrder && (
                  <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
                )}
                Save
              </span>
            </Button>
            <Button
              className="clear-button"
              onClick={() => {
                if (savingOrder) return;
                setReorderOpen(false);
                setReorderData([]);
                setDragSectionIndex(null);
                setDragVideoCtx({ sectionIndex: null, videoIndex: null });
              }}
              disabled={savingOrder}
            >
              Cancel
            </Button>
          </div>
        </AbsModal>
      )}
    </MDBContainer>
  );
};

export default VideoTutorials;
